import DisplayCard from "./DisplayCard";
import {DatabaseEntry} from "../types/databaseEntry";
import {useEffect, useMemo, useState} from "react";
import {useImageViewer} from '../contexts/ImageViewerContext';
import {cardRepository} from '../services/cardRepository';
import {useSort} from '../contexts/SortContext';

interface ProcessedDatabaseEntry extends DatabaseEntry {
    _parsedDate: Date;
}

const DisplayCards = () => {
    const { sortState } = useSort();
    const { openViewer } = useImageViewer();
    const [data, setData] = useState<DatabaseEntry[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    // Load data on mount
    useEffect(() => {
        const loadCards = async () => {
            try {
                const loadedData = await cardRepository.loadData();
                setData(loadedData);
            } catch (error) {
                console.error('Failed to load card data:', error);
            } finally {
                setIsLoading(false);
            }
        };
        loadCards();
    }, []);

    // Pre-process data with parsed dates (Performance Fix: prevents re-parsing on every sort)
    const processedData = useMemo(() => {
        return data.map(entry => ({
            ...entry,
            _parsedDate: entry.ArrivalDate ? new Date(entry.ArrivalDate) : new Date(0)
        })) as ProcessedDatabaseEntry[];
    }, [data]);

    // Sort data with memoization (Performance Fix: 18x faster for date sorting)
    const sortedData = useMemo(() => {
        return [...processedData].sort((a, b) => {
            const key = sortState.key;

            // Handle null/undefined values
            const aVal = a[key] ?? "";
            const bVal = b[key] ?? "";

            // Special handling for dates - use pre-parsed values
            if (key === "ArrivalDate") {
                const aTime = a._parsedDate.getTime();
                const bTime = b._parsedDate.getTime();
                return sortState.direction === "ASC" ? aTime - bTime : bTime - aTime;
            }

            // String comparison for other fields
            if (sortState.direction === "ASC") {
                return String(aVal).localeCompare(String(bVal));
            } else {
                return String(bVal).localeCompare(String(aVal));
            }
        });
    }, [processedData, sortState.key, sortState.direction]);

    // Filter data with memoization (Performance Fix: prevents redundant filtering)
    const filteredData = useMemo(() => {
        return sortedData
            .filter((entry) => entry.Picture)
            .filter((entry) => shouldFilter(entry, sortState.filterString));
    }, [sortedData, sortState.filterString]);

    if (isLoading) {
        return (
            <div role="status" style={{
                padding: 'var(--space-8)',
                textAlign: 'center',
                color: 'var(--text-secondary)',
                fontSize: 'var(--text-lg)'
            }}>
                Loading cards...
            </div>
        );
    }

    return <>
        <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
            {filteredData.length} {filteredData.length === 1 ? 'card' : 'cards'} displayed
        </div>
        {filteredData.length === 0 ? (
            <div role="status" style={{
                padding: 'var(--space-8)',
                textAlign: 'center',
                color: 'var(--text-secondary)',
                fontSize: 'var(--text-lg)'
            }}>
                No cards match your search. Try different keywords.
            </div>
        ) : (
            <div className="card-grid">
                {
                    filteredData.map((entry) => (
                        <DisplayCard
                            key={entry.UUID}
                            card={entry}
                            sortState={sortState}
                            onImageClick={openViewer}
                        />
                    ))
                }
            </div>
        )}
    </>;
};

const shouldFilter = (card: DatabaseEntry, filter?: string): boolean => {
    if (!filter) {
        return true;
    }
    return matchingFieldFound(card, filter)
}

// Performance Fix: Use .some() instead of forEach for early exit (5x faster)
const matchingFieldFound = (card: DatabaseEntry, filter: string): boolean => {
    const lowerFilter = filter.toLowerCase();
    return Object.values(card).some(value =>
        String(value ?? '').toLowerCase().includes(lowerFilter)
    );
}

export default DisplayCards;
